import React, { useState, useEffect, useCallback } from "react";
import { Redirect } from "react-router-dom";
import { Typography, Grid, Card, CardContent } from "@deere/fuel-react";
import { Stepper, Step, StepButton } from "@mui/material";
import { SELF_SERVICE_STEPS, SELFSERVICE_ADMIN, SELF_SERVICE_STEP_TITLES, SELF_SERVICE_TITLE_VIEW, SELF_SERVICE_TITLE_CREATE, SELF_SERVICE_TITLE_EDIT, SELFSERVICE_EMAIL_TEMPLATE_CODE, DEFAULT_LANGUAGE_ENGLISH, LBL_TEXT_HTML, LBL_LANGUAGESELECTION, LBL_ROWEMAILBODY, LBL_SELECTION, LBL_ROW, SELFSERVICE_DEFAULT_PAYLOAD, CRS_SURVEY_TEMPLATES } from "../../../utils/surveyLiterals";
import Steps from "./self-service-steps/Steps";
import { useUserInfo } from "../../../providers/userProvider";
import { ERR_TITLE_ACCESS } from "../../../utils/errorLiterals";
import CustomAlert from "../../../components/CustomAlert";
import { useTranslation } from "react-i18next";
import { useOktaAuth } from "@okta/okta-react";
import { getAvailableLanguages, getSelfServiceTemplateBySurveyCode } from "../../../services/survey.service";
import { validateHelpDocs, validateQuestionStep, validateSectionStep, validateSurveyNotification, validateSurveyTitleTranslations, validateReportConfiguration } from "../../../utils/validation";

const SelfServiceHome = (props) => {
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const { authState } = useOktaAuth();
  const [languageList, setLanguageSet] = useState([]);
  const [activeStep, setActiveStep] = useState(props.history.location.state?.step || 0);
  const [currentStep, setCurrentStep] = useState(SELF_SERVICE_STEPS.find((step) => step.id === activeStep));
  const [emailTemplateData, setEmailTemplateData] = useState([]);
  const [completedStep, setCompletedStep] = useState([]);
  const previousPage = props?.history?.location?.state?.previous;
  const [isViewOnly] = useState(props?.history?.location?.state?.stepTitle === SELF_SERVICE_STEP_TITLES.REVIEW_STEP && previousPage === "/selfServiceLanding" );
  const isEditMode = Boolean(props?.history?.location?.state?.surveyId);
  const [error, setError] = useState({ show: false, message: "" });
  const [fieldValidation, setFieldValidation] = useState(false);
  const [languageValidation, setLanguageValidation] = useState(false);
  const [questionStepError, setQuestionStepError] = useState({ show: false, message: "" });
  const [helpDocError, setHelpDocError] = useState({ show: false, message: "" });
  const [sectionError, setSectionError] = useState({ show: false, message: "" });
  const [translationValidation, setTranslationValidation] = useState(false);
  const [reportConfigurationError, setReportConfigurationError] = useState({ show: false, message: "" });
  const [payloadData, setPayloadData] = useState(props.history.location.state?.payloadData || SELFSERVICE_DEFAULT_PAYLOAD);
  const [emailTemplateBody, setEmailTemplateBody] = useState("");
  
  useEffect(() => {
    getAvailableLanguages(authState.accessToken.accessToken)
      .then((res) => {
        if (res && res.length > 0) setLanguageSet(res);
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    if (languageList.length > 0)
      getSelfServiceTemplateBySurveyCode(SELFSERVICE_EMAIL_TEMPLATE_CODE, authState.accessToken.accessToken)
        .then((res) => {
          if (res && Array.isArray(res) && res.length > 0) {
            const arrEmailContent = [];
            const parser = new DOMParser();
            const doc = parser.parseFromString(res[0].template, LBL_TEXT_HTML);
            const listItems = doc.getElementsByClassName(LBL_LANGUAGESELECTION);
            const tableRows = doc.getElementsByClassName(LBL_ROWEMAILBODY);
            languageList.sort((a, b) => (a.id > b.id ? 1 : -1)).forEach((lng) => {
              arrEmailContent.push({ language: lng.language, listItem: "", tableRow: "", content: "" });
            });
            for (let i=listItems.length-1; i>=0; i--) {
              const emailContentIndex = arrEmailContent.findIndex((aec) => `${LBL_SELECTION}${aec.language}` === listItems[i].id);
              if (emailContentIndex > -1) arrEmailContent[emailContentIndex].listItem = listItems[i].outerHTML;
              if (listItems[i].id !== `${LBL_SELECTION}${DEFAULT_LANGUAGE_ENGLISH}`) listItems[i].remove();
            }
            for (let i=tableRows.length-1; i>=0; i--) {
              const emailContentIndex = arrEmailContent.findIndex((aec) => `${LBL_ROW}${aec.language}` === tableRows[i].id);
              if (emailContentIndex > -1) arrEmailContent[emailContentIndex].tableRow = tableRows[i].outerHTML;
              if (tableRows[i].id !== `${LBL_ROW}${DEFAULT_LANGUAGE_ENGLISH}`)  tableRows[i].remove();
            }
            setEmailTemplateData(arrEmailContent);
            if (payloadData.surveyNotification === "")
              setPayloadData((prevState) => {
                return { ...prevState, surveyNotification: doc.documentElement.outerHTML };
              });
            setEmailTemplateBody(doc.documentElement.outerHTML);
          }
        })
        .catch((err) => console.log(err));
  }, [languageList]);

  useEffect(() => {
    const currentStep = SELF_SERVICE_STEPS.find((step) => step.id === activeStep);
    setCurrentStep(currentStep);
    if (!completedStep.includes(currentStep)) {
      setCompletedStep([...completedStep, currentStep])
    }
  }, [activeStep]);

  if (!userInfo || !userInfo.roles.includes(SELFSERVICE_ADMIN)) {
    return (
      <Redirect
        to={{
          pathname: "/error",
          state: { errorTitle: ERR_TITLE_ACCESS, errorUser: userInfo ? userInfo.userID : "" },
        }}
      />
    );
  }

  const isPartTemplate = payloadData?.templateType === CRS_SURVEY_TEMPLATES.PART;
  const visibleSteps = SELF_SERVICE_STEPS.filter(
    (step) => step.title !== SELF_SERVICE_STEP_TITLES.REPORT_CONFIGURATION_STEP || isPartTemplate
  );
  const visibleIndex = currentStep ? visibleSteps.findIndex((step) => step.id === currentStep.id) : -1;
  const displayStep = currentStep && visibleIndex >= 0
    ? { ...currentStep, step: `Step ${visibleIndex + 1}` }
    : currentStep;

  const navigationToStep =  useCallback((step) => {
    const sectionCurrentStepId = SELF_SERVICE_STEPS.find((step) => step.id === activeStep).id;
    const nextStep = SELF_SERVICE_STEPS.find((selfStep) => selfStep.id === step.id).id;
    if (completedStep.length > 2){
      const error = validateSurveyTitleTranslations(payloadData);
      if (error.show){
        setTranslationValidation(error)
        setActiveStep(2);
        return
      } 
    } if(completedStep.length > 3) {
      const error = validateSectionStep(payloadData);
      if (error.show){
        setSectionError(error)
        setActiveStep(3);
        return
      } 
    } if(completedStep.length > 4) {
      const error = validateQuestionStep(payloadData);
      if (error.show){
        setQuestionStepError(error)
        setActiveStep(4);
        return
      } 
    } if(isPartTemplate && completedStep.length > 7) {
      const error = validateReportConfiguration(payloadData);
      if (error.show){
        setReportConfigurationError(error)
        setActiveStep(8);
        return
      } 
    }
    if(nextStep < sectionCurrentStepId){
      setActiveStep(nextStep);
    } else {
    switch(sectionCurrentStepId) {
      case 0:
        if (payloadData && payloadData.description === ''){
          setFieldValidation(true)
        } else {
          setFieldValidation(false)
          setActiveStep(nextStep);
        }
        break;
      case 1:
        if (payloadData && payloadData.language && payloadData.language.length === 0 && payloadData.description === '') {
          setLanguageValidation(true)
        } else {
          setLanguageValidation(false);
          setActiveStep(nextStep);
          
        }
        break;
      case 2:        
        const error = validateSurveyTitleTranslations(payloadData);
        if(error.show && payloadData && payloadData.language.length === 0 && payloadData.description === ''){
          setTranslationValidation(error)
        } else{
          setTranslationValidation(false)
          setActiveStep(nextStep);
        }
        break;
      case 3:
        const sectionErr = validateSectionStep(payloadData);
        if(sectionErr.show && error.show && payloadData && payloadData.language.length === 0 && payloadData.description === ''){  
          setSectionError(sectionErr)
        } else {
          setSectionError({show: false})
          setActiveStep(nextStep);
        }
      break
      case 4:
        const questionError = validateQuestionStep(payloadData);
        if(questionError.show){
          setQuestionStepError(questionError)
        } else{
          setQuestionStepError({ show: false})
          setActiveStep(nextStep);
        }
      break
      case 5:
        const helpError = validateHelpDocs(payloadData);
        if(helpError.show){
          setHelpDocError(helpError)
        } else{
          setHelpDocError({show: false})
          setActiveStep(nextStep);
        }
      break
      case 6:
        const notificationError = validateSurveyNotification(payloadData);
        if(notificationError.show){
          setError(notificationError)
        } else{
          setError({show: false})
          setActiveStep(nextStep);
        }
      break
      case 8:
        const reportConfigError = validateReportConfiguration(payloadData);
        if(reportConfigError.show){
          setReportConfigurationError(reportConfigError)
        } else{
          setReportConfigurationError({show: false})
          setActiveStep(nextStep);
        }
      break
      default:
         setActiveStep(nextStep);
          break;
      }
    }
  }, [activeStep, payloadData])

  return (
    <>
      <Typography variant="h1" size="3xl" color="#A0A0A0" mb={4}>
        {isViewOnly ? SELF_SERVICE_TITLE_VIEW : isEditMode ? SELF_SERVICE_TITLE_EDIT : SELF_SERVICE_TITLE_CREATE}
      </Typography>
      <Grid container spacing={4}>
      {!isViewOnly && (
          <Grid item xs={2}>
            <Card>
              <CardContent>
                <Stepper orientation="vertical" activeStep={(completedStep.length -1)}>
                  {visibleSteps.map((step) => (
                   <Step key={step.id}>
                    <StepButton onClick={() => navigationToStep(step)}>{step.title}</StepButton>
                    </Step>
                  ))}
                </Stepper>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={isViewOnly ? 12 : 10}>
          {currentStep && (
            <Steps
              error={error}
              setError={setError}
              setLanguageValidation={setLanguageValidation}
              languageValidation={languageValidation}
              translationValidation={translationValidation}
              setQuestionStepError={setQuestionStepError}
              questionStepError={questionStepError}
              setHelpDocError={setHelpDocError}
              helpDocError= {helpDocError}
              setSectionError={setSectionError}
              sectionError={sectionError}
              fieldValidation={fieldValidation}
              setFieldValidation={setFieldValidation}
              reportConfigurationError={reportConfigurationError}
              setReportConfigurationError={setReportConfigurationError}
              currentStep={displayStep}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              payloadData={payloadData}
              setPayloadData={setPayloadData}
              languageList={languageList}
              isViewOnly={isViewOnly}
              emailTemplateData={emailTemplateData}
              emailTemplateBody={emailTemplateBody}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default SelfServiceHome;
